import { createSlice, createSelector } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';

import { PaymentContext } from 'modules/invoices/hooks';
import type { AppState } from 'src/core/reducers';
import { getCompanyCurrency } from 'src/core/selectors/globalSelectors';

import type { VerificationOfPayee } from '../../models';
import {
  getSelectedPaymentsToSchedule,
  getTotalAmountOfSelectedPaymentsToPayFromWallet,
} from '../../redux/selectors';
import { type Transfer, sumTransfersAmount } from '../models';
import { filterInvalidVerificationOfPayeeTransfers } from '../utils';

type Type = 'invoice' | 'expense';
type TransferState = {
  [type in Type]: {
    transfersSelected: Transfer[];
  };
};

const initialState: TransferState = {
  expense: {
    transfersSelected: [],
  },
  invoice: {
    transfersSelected: [],
  },
};

const transfersSlice = createSlice({
  name: 'invoices/transfers',
  initialState,
  reducers: {
    setTransfersSelected: (
      state,
      { payload }: PayloadAction<{ transfersSelected: Transfer[]; type: Type }>,
    ) => {
      state[payload.type].transfersSelected = payload.transfersSelected;
    },

    resetTransfersSelection: (
      state,
      { payload }: PayloadAction<{ type: Type }>,
    ) => {
      state[payload.type].transfersSelected = [];
    },
    unselectTransfers: (
      state,
      {
        payload,
      }: PayloadAction<{ transfersToUnselect: Transfer[]; type: Type }>,
    ) => {
      state[payload.type].transfersSelected = state[
        payload.type
      ].transfersSelected.filter((selectedTransfer) => {
        return !payload.transfersToUnselect.some(
          (transferToRemove) => transferToRemove.id === selectedTransfer.id,
        );
      });
    },
    updateTransferVerificationOfPayee: (
      state,
      {
        payload,
      }: PayloadAction<{
        transfers: {
          transferId: string;
          verificationOfPayee: VerificationOfPayee | undefined;
        }[];
        type: Type;
      }>,
    ) => {
      state[payload.type].transfersSelected = state[
        payload.type
      ].transfersSelected.map((transfer) => {
        const verificationOfPayeeResult = payload.transfers.find(
          (t) => t.transferId === transfer.id,
        );
        return verificationOfPayeeResult
          ? {
              ...transfer,
              verificationOfPayee:
                verificationOfPayeeResult.verificationOfPayee,
            }
          : transfer;
      });
    },
  },
});

const selectTransfersSlice = (state: AppState): TransferState =>
  state.invoices.transfers;

const selectTransfersStateForType = createSelector(
  selectTransfersSlice,
  (_, type: Type) => type,
  (transfersSliceState: TransferState, type: Type) => transfersSliceState[type],
);
const selectTransfersSelected = createSelector(
  selectTransfersStateForType,
  (transfersSliceState) => transfersSliceState.transfersSelected,
);

const selectTransfersSelectedCount = createSelector(
  selectTransfersSelected,
  (transfersSelected) => transfersSelected.length,
);

const selectTransfersSelectedAmountSum = createSelector(
  [selectTransfersSelected, getCompanyCurrency],
  (transfersSelected, companyCurrency) => {
    return sumTransfersAmount(transfersSelected, companyCurrency);
  },
);

const totalOutgoingAmountBasedOnPage = (isScheduling: boolean) =>
  createSelector(
    [
      getTotalAmountOfSelectedPaymentsToPayFromWallet,
      selectTransfersSelectedAmountSum,
    ],
    (schedulePageSelectedTotal, confirmPageSelectedTotal) => {
      return isScheduling
        ? schedulePageSelectedTotal
        : confirmPageSelectedTotal;
    },
  );

const selectTransfersByType = (isScheduling: boolean) =>
  createSelector(
    [
      getSelectedPaymentsToSchedule,
      (state) => selectTransfersSelected(state, 'invoice'),
    ],
    (schedulingTransfers, confirmTransfers) =>
      isScheduling ? schedulingTransfers : confirmTransfers,
  );

const selectTransfersWithInvalidVerificationOfPayee = createSelector(
  selectTransfersSelected,
  (transfersSelected): Transfer[] =>
    filterInvalidVerificationOfPayeeTransfers(transfersSelected),
);

export const selectors = {
  totalOutgoingAmountBasedOnPage,
  selectTransfersSelected,
  selectTransfersSelectedCount,
  selectTransfersSelectedAmountSum,
  selectTransfersByType,
  selectTransfersWithInvalidVerificationOfPayee,
};

export const useSelectorWithContext = <T>(
  selector: (state: AppState, type: Type) => T,
): T => {
  const { type } = PaymentContext.useContext();
  return useSelector((state: AppState) => selector(state, type));
};

export const { actions, reducer } = transfersSlice;
